import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  Wrench,
  BadgeCheck,
  Check,
} from "lucide-react";
import { api, currency } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { APPLIANCES, APPLIANCE_ICONS, APPLIANCE_IMAGES } from "@/lib/appliances";

const IMG =
  "https://images.pexels.com/photos/6471913/pexels-photo-6471913.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=940";

const STEPS = [
  { n: "01", t: "Enquire or pick a plan", d: "Tell us your appliances and home size, or choose a plan directly." },
  { n: "02", t: "Get your Customer ID", d: "Register in seconds and receive a unique ID for your household." },
  { n: "03", t: "We keep it running", d: "Certified technicians handle scheduled care and repairs, on time." },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Home() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(242,106,33,0.10),transparent_55%)]" />
        <div className="relative max-w-[1300px] mx-auto px-5 sm:px-8 pt-12 md:pt-20 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left */}
            <div>
              <motion.p
                className="overline text-[#8CA0B4] mb-6"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Annual Maintenance Plans · Hyderabad
              </motion.p>

              <motion.h1
                className="font-heading font-bold text-5xl sm:text-6xl lg:text-[4.4rem] leading-[1.03] tracking-tight text-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.05 }}
              >
                Keep every appliance{" "}
                <span className="text-[#F26A21]">running like new</span> — all
                year long.
              </motion.h1>

              <motion.p
                className="text-[#AEBECD] text-lg leading-relaxed max-w-xl mt-7"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
              >
                ASA Home Appliance Services offers three yearly maintenance
                plans starting at just ₹799. Certified technicians. Genuine
                parts. Fixed pricing. No last-minute surprises.
              </motion.p>

              <motion.div
                className="flex flex-wrap gap-4 mt-9"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
              >
                <Link
                  to="/plans"
                  data-testid="hero-view-plans"
                  className="inline-flex items-center gap-2 bg-[#F26A21] text-white px-7 py-4 rounded-full font-semibold hover:bg-[#DE5C15] transition-colors duration-300 group"
                >
                  See Plans
                  <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/lookup"
                  data-testid="hero-lookup"
                  className="inline-flex items-center gap-2 bg-white text-[#0C1C2E] px-7 py-4 rounded-full font-semibold hover:bg-[#EEF2F6] transition-colors duration-300"
                >
                  I have a Customer ID
                </Link>
              </motion.div>

              <motion.div
                className="flex flex-wrap gap-10 mt-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {[
                  ["5000+", "Homes serviced"],
                  ["24 hr", "Response time"],
                  ["4.8★", "Customer rating"],
                ].map(([v, l]) => (
                  <div key={l}>
                    <p className="font-heading font-extrabold text-3xl text-white">{v}</p>
                    <p className="overline text-[#6B7E92] mt-1">{l}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right image */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            >
              <div className="rounded-3xl overflow-hidden border border-[#223851] aspect-[4/3.4]">
                <img
                  src={IMG}
                  alt="ASA certified technician servicing an appliance"
                  className="w-full h-full object-cover"
                />
              </div>
              <motion.div
                className="absolute -bottom-5 left-5 sm:left-8 bg-white rounded-2xl shadow-2xl px-5 py-4 flex items-center gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                data-testid="hero-badge-card"
              >
                <span className="w-11 h-11 rounded-full bg-[#F26A21] flex items-center justify-center shrink-0">
                  <BadgeCheck size={22} className="text-white" />
                </span>
                <div>
                  <p className="font-heading font-bold text-[#0C1C2E] leading-tight">Certified Care</p>
                  <p className="text-sm text-[#6B7E92]">Trained by OEM</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 border-y border-[#1B3350] bg-[#0A1626]">
        <Marquee items={APPLIANCES.map((a) => a.toUpperCase())} />
      </section>

      {/* APPLIANCES */}
      <section className="max-w-[1300px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <Reveal>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-white leading-tight">
            Nine appliances. One team.{" "}
            <span className="text-[#F26A21]">Zero excuses.</span>
          </h2>
          <p className="text-[#AEBECD] text-lg max-w-2xl mt-5 leading-relaxed">
            Every plan can cover any of the appliances below. Our technicians
            are trained across brands and models — from single-door fridges to
            four-burner chimneys.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-5 mt-12">
          {APPLIANCES.map((a, i) => {
            const Icon = APPLIANCE_ICONS[a];
            const img = APPLIANCE_IMAGES[a];
            return (
              <Reveal key={a} delay={(i % 3) * 0.06}>
                <div
                  data-testid={`appliance-tile-${a}`}
                  className="group rounded-2xl overflow-hidden bg-[#122539] border border-[#223851] hover:border-[#F26A21] transition-colors duration-300"
                >
                  <div className={`aspect-[4/3] flex items-center justify-center overflow-hidden ${img ? "bg-white" : "bg-[#0C1C2E]"}`}>
                    {img ? (
                      <img
                        src={img}
                        alt={a}
                        loading="lazy"
                        className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      Icon && <Icon size={44} className="text-[#F26A21]" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex items-center justify-between px-4 py-3.5 border-t border-[#223851]">
                    <span className="font-heading font-semibold text-white text-sm sm:text-base">{a}</span>
                    <span className="text-[10px] uppercase tracking-widest text-[#F26A21] font-semibold">Covered</span>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHY ASA */}
      <section className="bg-[#0A1626] border-y border-[#1B3350] py-24 md:py-32">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-[#223851] aspect-[5/4]">
              <img
                src="https://images.pexels.com/photos/5463581/pexels-photo-5463581.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="ASA technician at work"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="overline text-[#F26A21] mb-4">Why ASA</p>
              <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-white leading-tight mb-4">
                A service team on retainer for your whole home.
              </h2>
            </Reveal>
            <div className="space-y-5 mt-8">
              {[
                { icon: ShieldCheck, t: "Preventive by design", d: "Scheduled maintenance stops breakdowns before they happen." },
                { icon: Clock, t: "Priority response", d: "Plan members jump the queue with same-day and 24-hour SLAs." },
                { icon: Wrench, t: "Genuine parts, fixed pricing", d: "Only verified spares, transparent bills, no surprises." },
              ].map((f, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="flex gap-4 rounded-2xl bg-[#122539] border border-[#223851] p-5">
                    <span className="w-11 h-11 rounded-xl bg-[#F26A21]/15 flex items-center justify-center shrink-0">
                      <f.icon className="text-[#F26A21]" size={22} />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-white">{f.t}</h3>
                      <p className="text-[#AEBECD] mt-1 leading-relaxed">{f.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PLANS PREVIEW */}
      <section className="max-w-[1300px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <Reveal>
            <p className="overline text-[#F26A21] mb-3">Plans</p>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-white leading-tight">
              Choose your cover.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/plans"
              data-testid="home-all-plans"
              className="inline-flex items-center gap-2 font-semibold text-white hover:text-[#F26A21] transition-colors group"
            >
              Compare all plans
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <div
                data-testid={`home-plan-${p.slug}`}
                className={`h-full flex flex-col rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                  p.popular
                    ? "border-[#F26A21] bg-[#122539] ring-1 ring-[#F26A21]/40"
                    : "border-[#223851] bg-[#0F2236] hover:border-[#33506e]"
                }`}
              >
                {p.popular && (
                  <span className="self-start overline text-white bg-[#F26A21] px-3 py-1 rounded-full mb-3">
                    Most popular
                  </span>
                )}
                <h3 className="font-heading font-bold text-2xl text-white">{p.name}</h3>
                <p className="text-[#AEBECD] mt-2 text-sm min-h-[42px]">{p.tagline}</p>
                <p className="font-heading font-extrabold text-4xl text-white mt-6">
                  ₹{currency(p.price)}
                  <span className="text-base font-medium text-[#6B7E92]">/{p.period}</span>
                </p>
                <p className="overline text-[#6B7E92] mt-3">
                  {p.appliances_covered} · {p.visits_per_year}
                </p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.slice(0, 4).map((f, fi) => (
                    <li key={fi} className="flex gap-2.5 text-sm text-[#D3DEE8]">
                      <Check size={16} className="text-[#F26A21] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/get-id?plan=${p.slug}`}
                  data-testid={`home-plan-cta-${p.slug}`}
                  className={`mt-7 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-colors duration-300 ${
                    p.popular
                      ? "bg-[#F26A21] text-white hover:bg-[#DE5C15]"
                      : "bg-white/5 text-white border border-[#33506e] hover:bg-[#F26A21] hover:border-[#F26A21]"
                  }`}
                >
                  Choose {p.name}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0A1626] border-t border-[#1B3350] py-24 md:py-32">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="overline text-[#F26A21] mb-3">How it works</p>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-white leading-tight mb-12">
              Three steps to a maintained home.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="rounded-3xl bg-[#122539] border border-[#223851] p-8 h-full">
                  <span className="inline-flex w-12 h-12 rounded-2xl bg-[#F26A21]/15 items-center justify-center font-heading font-bold text-[#F26A21] text-xl">
                    {s.n}
                  </span>
                  <h3 className="font-heading font-semibold text-xl text-white mt-6">{s.t}</h3>
                  <p className="text-[#AEBECD] mt-2 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/get-id"
              data-testid="home-get-started"
              className="inline-flex items-center gap-2 mt-12 bg-[#F26A21] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#DE5C15] transition-colors duration-300 group"
            >
              Get your Customer ID
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
